import { ChangeDetectionStrategy, Component, ComponentRef, inject, OnInit, signal } from '@angular/core';
import { MODAL_REF, ModalRef, ModalService } from '@ui/modal/modal.service';
import { ModalComponent } from '@ui/modal/modal.component';
import { InputComponent } from '@ui/input/input.component';
import { FormControl, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { TendersService } from '@api/services/tenders.service';
import { Tender, CsvInvoiceArticle } from '@api/models/tender.model';
import { CommonModule } from '@angular/common';
import { CardComponent } from '@ui/card/card.component';
import { LoaderComponent } from '@ui/loader/loader.component';
import { ButtonComponent } from '@ui/button/button.component';
import { ConfirmService } from '@ui/confirm-modal/confirm.service';
import { ArticleFormModalComponent } from './article-form-modal.component';

@Component({
  selector: 'app-tenders-modal',
  standalone: true,
  imports: [ModalComponent, InputComponent, ReactiveFormsModule, CommonModule, CardComponent, LoaderComponent, ButtonComponent],
  template: `
    <ui-modal
      [title]="tender?.id ? 'Aanbesteding bewerken' : 'Nieuwe aanbesteding'"
      [submitText]="'Opslaan'"
      [cancelText]="'Annuleren'"
      (close)="closeModal()"
      (save)="saveModal()"
      [size]="tender?.id ? 'lg' : 'md'"
    >
      <!-- Loader voor het laden van tender details -->
      <div *ngIf="loadingTenderDetails()" class="flex justify-center py-8">
        <ui-loader />
      </div>

      <!-- Formulier -->
      <form [formGroup]="form" *ngIf="!loadingTenderDetails()">
        <div class="flex flex-col gap-4">
          <ui-input
            id="name"
            label="Naam"
            placeholder="Naam van de aanbesteding"
            formControlName="name"
            [required]="true"
          />

          <ui-input
            id="end_date"
            label="Einddatum"
            type="date"
            formControlName="end_date"
            [required]="true"
          />

          <ui-input
            id="zoho_category_fieldname"
            label="Zoho Categorie Veld"
            placeholder="Bijv. Categorie_Apeldoorn"
            formControlName="zoho_category_fieldname"
            [required]="true"
          />
        </div>
      </form>

      <!-- Producten sectie (alleen voor bestaande tenders) -->
      <div *ngIf="tender && tender.id && !loadingTenderDetails()" class="mt-6">
        <ui-card>
          <div class="mb-4 flex justify-between items-start">
            <div>
              <h3 class="text-lg font-medium text-slate-900">Producten</h3>
              <p class="text-sm text-slate-600">Gekoppelde factuurartikelen voor deze aanbesteding</p>
            </div>
            <ui-button
              [icon]="'add'"
              [variant]="'primary'"
              [size]="'sm'"
              (onClick)="addArticle()"
              [disabled]="deletingArticleId() !== null"
            >
              Artikel toevoegen
            </ui-button>
          </div>

          <!-- Producten lijst -->
          <div *ngIf="tender.csv_invoice_articles && tender.csv_invoice_articles.length > 0" class="space-y-3">
            <div *ngFor="let article of tender.csv_invoice_articles"
                 class="flex items-center justify-between p-3 bg-slate-50 rounded-lg border">
              <div class="flex-1">
                <div class="font-medium text-slate-900">{{ article.product_name }}</div>
                <div class="text-sm text-slate-600">Code: {{ article.product_code }}</div>
              </div>
              <div class="flex items-center gap-3">
                <span class="inline-flex items-center px-2 py-1 text-xs font-medium rounded-full bg-blue-100 text-blue-800">
                  {{ article.unit_type }}
                </span>
                <ui-button
                  [icon]="'delete'"
                  [variant]="'danger'"
                  [size]="'sm'"
                  [hideLabel]="true"
                  (onClick)="deleteArticle(article)"
                  [disabled]="deletingArticleId() === article.id"
                  [loading]="deletingArticleId() === article.id"
                >
                  Verwijderen
                </ui-button>
              </div>
            </div>
          </div>

          <!-- Geen producten bericht -->
          <div *ngIf="!tender.csv_invoice_articles || tender.csv_invoice_articles.length === 0" class="text-center py-6">
            <h3 class="text-lg font-medium text-slate-900 mb-2">Geen producten</h3>
            <p class="text-sm text-slate-600">Er zijn nog geen factuurartikelen gekoppeld aan deze aanbesteding.</p>
          </div>
        </ui-card>
      </div>
    </ui-modal>
  `,
  styles: `
    :host {
      display: block;
    }
  `,
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class TendersModalComponent implements OnInit {
  private modalRef = inject(MODAL_REF);
  modalService = inject(ModalService);
  tendersService = inject(TendersService);
  confirmService = inject(ConfirmService);

  tender: Tender | null = null;
  form: FormGroup = new FormGroup({});
  loadingTenderDetails = signal<boolean>(false);
  deletingArticleId = signal<number | null>(null);

  ngOnInit() {
    // Controleer of er tender data is doorgegeven en of het een bestaande tender is
    if (this.tender?.id) {
      // Voor bestaande tender: haal details op via show endpoint
      this.loadTenderDetails(this.tender.id);
    } else {
      // Voor nieuwe tender: gebruik de basis data
      this.initializeForm();
    }
  }

  private loadTenderDetails(tenderId: string) {
    this.loadingTenderDetails.set(true);
    this.tendersService.getTender(tenderId).subscribe({
      next: (tender) => {
        this.tender = tender;
        this.initializeForm();
        this.loadingTenderDetails.set(false);
      },
      error: (error) => {
        console.error('Error loading tender details:', error);
        this.loadingTenderDetails.set(false);
        // Fallback naar de doorgegeven basis data, maar dan zonder producten
        this.initializeForm();
      }
    });
  }

  private initializeForm() {
    this.form = new FormGroup({
      name: new FormControl(this.tender?.name, Validators.required),
      end_date: new FormControl(this.tender?.end_date, Validators.required),
      zoho_category_fieldname: new FormControl(this.tender?.zoho_category_fieldname, Validators.required),
    });
  }

  addArticle() {
    if (!this.tender?.id) return;

    const modalRef = this.modalService.open(ArticleFormModalComponent, {
      data: {
        tenderId: this.tender.id,
      },
    });

    modalRef.onClose.subscribe(() => {
      // Refresh tender details om nieuwe artikelen te tonen
      if (this.tender?.id) {
        this.loadTenderDetails(this.tender.id);
      }
    });
  }

  deleteArticle(article: CsvInvoiceArticle) {
    const confirm = this.confirmService.confirm({
      title: 'Artikel verwijderen',
      message: `Weet u zeker dat u "${article.product_name}" wilt verwijderen?`,
      confirmText: 'Verwijderen',
      cancelText: 'Annuleren',
    });

    confirm.then((confirmed) => {
      if (confirmed) {
        this.deletingArticleId.set(article.id);
        this.tendersService.deleteCsvInvoiceArticle(article.id).subscribe({
          next: () => {
            this.deletingArticleId.set(null);
            // Refresh tender details om updated lijst te tonen
            if (this.tender?.id) {
              this.loadTenderDetails(this.tender.id);
            }
          },
          error: (error) => {
            console.error('Error deleting article:', error);
            this.deletingArticleId.set(null);
            // TODO: Show error notification
          }
        });
      }
    });
  }

  closeModal() {
    if (this.modalRef) {
      this.modalService.close(this.modalRef as ModalRef<any>);
    }
  }

  saveModal() {
    this.form.markAllAsTouched();

    if (this.form.invalid) {
      return;
    }

    if (this.tender?.id) {
      this.tendersService.updateTender(this.tender.id, this.form.value as Tender).subscribe(() => {
        this.closeModal();
      });
    } else {
      this.tendersService.createTender(this.form.value as Tender).subscribe(() => {
        this.closeModal();
      });
    }
  }
}
